import { Check, CloudOff, RefreshCw } from 'lucide-react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import * as React from 'react'

import { cn } from '../lib/utils'

/**
 * The five connection phases for a reactive (live-subscription) data screen. `live` renders
 * nothing; the other four each render a banner. Derived from the subscription lifecycle by
 * the app, not stored per-screen.
 *
 * `stalled` and `unrecoverable` share the amber persistent card and differ only in what they
 * offer: `stalled` is still trying and shows a retry, `unrecoverable` has given up and shows a
 * reload. That distinction matters — a banner that retries forever is indistinguishable from a
 * broken one.
 */
export type ConnectionPhase = 'live' | 'reconnecting' | 'restored' | 'stalled' | 'unrecoverable'

/**
 * Resolved copy for the banner. `@parsleyai/ui` is i18n-free — the app passes already-translated
 * strings (from the `connection.*` namespace) so the component stays purely presentational.
 */
export interface ConnectionBannerLabels {
  /** `reconnecting` — e.g. "Reconnecting…" */
  reconnecting: string
  /** `restored` — e.g. "Back online" */
  restored: string
  /** `stalled.title` — e.g. "Still trying to reconnect…" */
  stalledTitle: string
  /** `stalled.body` — reassurance, e.g. "Your data is saved and up to date." */
  stalledBody: string
  /** `unrecoverable.title` — e.g. "Couldn't reconnect" */
  unrecoverableTitle: string
  /** `unrecoverable.body` — reassurance + the way out, e.g. "Your data is safe. Reload to…" */
  unrecoverableBody: string
  /** `retry` — the manual-recovery action, e.g. "Try now" */
  retry: string
  /** `reload` — the terminal action, e.g. "Reload" */
  reload: string
}

export interface ConnectionBannerProps {
  /** Current connection phase. `live` renders nothing. */
  phase: ConnectionPhase
  /** Resolved copy (app-translated). */
  labels: ConnectionBannerLabels
  /** Manual recovery — wired to the "Try now" action shown only in the `stalled` phase. */
  onRetry?: () => void
  /** Full reload — wired to the "Reload" action shown only in the `unrecoverable` phase. */
  onReload?: () => void
  /** Applied to the outer `role="status"` region (e.g. horizontal insets from the page). */
  className?: string
}

// Eased-inset timing: the banner reveals/collapses by animating its height (0 ↔ auto), so the
// content below slides down/up rather than jumping. The design's scroll-container inset, adapted
// for a body-scroll page where the banner lives in normal flow.
const INSET_EASE: [number, number, number, number] = [0.22, 0.68, 0, 1]

export const ConnectionBanner = React.forwardRef<HTMLDivElement, ConnectionBannerProps>(
  ({ phase, labels, onRetry, onReload, className }, ref) => {
    const reduced = useReducedMotion()
    const isUnrecoverable = phase === 'unrecoverable'
    // Both persistent phases render the same amber card; only the copy and the action differ.
    const isPersistent = phase === 'stalled' || isUnrecoverable

    const tone =
      phase === 'restored'
        ? 'bg-brand-green-50 dark:bg-brand-green-900/60 border-brand-green-200 dark:border-brand-green-800'
        : isPersistent
          ? 'bg-amber-wash border-amber-hairline'
          : 'bg-card border-border'

    return (
      // biome-ignore lint/a11y/useSemanticElements: a polite status live-region container that hosts animated banner content + page insets; <output> is a form-result element with the wrong semantics.
      <div ref={ref} role="status" aria-live="polite" className={cn('flex justify-center', className)}>
        {/* Outer wrapper eases height 0 ↔ auto so appear/disappear never jumps the content below.
            Stable key: it stays mounted across phase changes (content swaps in place), and the whole
            thing collapses only on the return to `live`. */}
        <AnimatePresence initial={false}>
          {phase !== 'live' && (
            <motion.div
              key="banner"
              className="w-full overflow-hidden"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={reduced ? { duration: 0.001 } : { height: { duration: 0.36, ease: INSET_EASE }, opacity: { duration: 0.3 } }}>
              {/* Padding inside the overflow-hidden clip so the pill's `shadow-card` isn't cut off
                  (top/sides/bottom room for the ~28px feathered drop shadow). */}
              <div className="flex justify-center px-3 pt-2 pb-5">
                <div className={cn('border shadow-card', isPersistent ? 'max-w-md rounded-2xl' : 'max-w-full rounded-full', tone)}>
                  {phase === 'reconnecting' && (
                    <div className="flex items-center gap-2.5 px-3.5 py-2.5">
                      <motion.span
                        aria-hidden
                        className="inline-block h-[15px] w-[15px] shrink-0 rounded-full border-2 border-brand-green-600/25 border-t-brand-green-600 dark:border-brand-green-400/25 dark:border-t-brand-green-400"
                        animate={{ rotate: 360 }}
                        transition={{ duration: reduced ? 2.4 : 0.9, ease: 'linear', repeat: Number.POSITIVE_INFINITY }}
                      />
                      <span className="text-sm font-medium text-muted-foreground">{labels.reconnecting}</span>
                    </div>
                  )}

                  {phase === 'restored' && (
                    <div className="flex items-center gap-2.5 px-3.5 py-2.5">
                      <span className="inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-brand-green-600 text-white dark:bg-brand-green-600">
                        <Check className="h-2.5 w-2.5" strokeWidth={2.4} />
                      </span>
                      <span className="text-sm font-semibold text-brand-green-700 dark:text-brand-green-300">{labels.restored}</span>
                    </div>
                  )}

                  {isPersistent && (
                    <div className="flex items-start gap-2.5 px-3.5 py-2.5">
                      <CloudOff className="mt-px h-[17px] w-[17px] shrink-0 text-amber-strong" strokeWidth={1.8} />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold leading-snug text-amber-strong">
                          {isUnrecoverable ? labels.unrecoverableTitle : labels.stalledTitle}
                        </div>
                        <div className="mt-0.5 text-xs leading-snug text-amber-strong opacity-80">
                          {isUnrecoverable ? labels.unrecoverableBody : labels.stalledBody}
                        </div>
                      </div>
                      {/* Hand-rolled: the DS has no amber/warning pill tone (PillButton tops out at rose
                          `danger`), and no h-8 pill size. Flagged as a design-system gap in the PR. */}
                      <button
                        type="button"
                        onClick={isUnrecoverable ? onReload : onRetry}
                        className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full border-[1.5px] border-amber-hairline bg-transparent px-3 text-xs font-semibold text-amber-strong transition-colors hover:bg-amber-wash active:translate-y-px motion-reduce:active:translate-y-0 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-pelorous-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background dark:focus-visible:ring-pelorous-300">
                        <RefreshCw className="h-3.5 w-3.5" /> {isUnrecoverable ? labels.reload : labels.retry}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    )
  }
)
ConnectionBanner.displayName = 'ConnectionBanner'
